import React, { useEffect, useState } from 'react';

import PersonAdd from '@mui/icons-material/PersonAdd';
import Save from '@mui/icons-material/Save';
import { Alert, Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { ViewTitleBar } from '@@components';

import { ApiException, ProfileClient, UserProfileModel } from '../../api/api';
import InviteUserDialog from './InviteUserDialog';

export default function ProfileView() {
	const profileClient = useApiClient(ProfileClient);
	const toast = useToastService();
	const [profile, setProfile] = useState<UserProfileModel | null>(null);
	const [displayName, setDisplayName] = useState('');
	const [givenName, setGivenName] = useState('');
	const [surname, setSurname] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const displayNameError = Boolean(profile) && !displayName.trim();
	const givenNameError = Boolean(profile) && !givenName.trim();
	const surnameError = Boolean(profile) && !surname.trim();
	const hasRequiredProfileFields = Boolean(displayName.trim()) && Boolean(givenName.trim()) && Boolean(surname.trim());

	useEffect(() => {
		const load = async () => {
			try {
				const data = await profileClient.getProfile();
				setProfile(data);
				setDisplayName(data.displayName || '');
				setGivenName(data.givenName || '');
				setSurname(data.surname || '');
			} catch (e) {
				setError(getErrorMessage(e));
			}
		};

		load();
	}, [profileClient]);

	const handleSave = async () => {
		if (!hasRequiredProfileFields) {
			return;
		}

		setIsSaving(true);
		setError(null);
		try {
			await profileClient.updateProfile({ displayName: displayName.trim(), givenName: givenName.trim(), surname: surname.trim() });
			toast.success('Profile updated');
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<>
			<ViewTitleBar title="Profile" />
			<Box sx={{ p: 3, maxWidth: 760, width: '100%' }}>
				<Paper variant="outlined" sx={{ p: 3 }}>
					<Stack spacing={2.5}>
						{error && <Alert severity="error">{error}</Alert>}
						<Box>
							<Typography variant="h6">Profile</Typography>
							<Typography color="text.secondary">Email: {profile?.email}</Typography>
							{profile?.tenantCode && <Typography color="text.secondary">Organization: {profile.tenantCode}</Typography>}
						</Box>
						<Box>
							<Typography variant="h6">Details</Typography>
						</Box>
						<TextField
							label="Display name"
							name="randol-profile-display"
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							autoComplete="new-password"
							required
							error={displayNameError}
							helperText={displayNameError ? 'Display name is required.' : undefined}
						/>
						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
							<TextField
								label="First name"
								name="randol-profile-given"
								value={givenName}
								onChange={(e) => setGivenName(e.target.value)}
								autoComplete="new-password"
								required
								error={givenNameError}
								helperText={givenNameError ? 'First name is required.' : undefined}
								fullWidth
							/>
							<TextField
								label="Last name"
								name="randol-profile-surname"
								value={surname}
								onChange={(e) => setSurname(e.target.value)}
								autoComplete="new-password"
								required
								error={surnameError}
								helperText={surnameError ? 'Last name is required.' : undefined}
								fullWidth
							/>
						</Stack>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
							<Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={isSaving || !profile || !hasRequiredProfileFields}>
								Save
							</Button>
						</Box>

						<Divider />

						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
							<Box>
								<Typography variant="h6">Invite User</Typography>
								<Typography color="text.secondary">Create an organization invitation link for a new user.</Typography>
							</Box>
							<Button variant="outlined" startIcon={<PersonAdd />} onClick={() => setIsInviteDialogOpen(true)}>
								Invite user
							</Button>
						</Stack>
					</Stack>
				</Paper>
			</Box>
			{isInviteDialogOpen && <InviteUserDialog onClose={() => setIsInviteDialogOpen(false)} />}
		</>
	);
}

function getErrorMessage(error: unknown) {
	if (ApiException.isApiException(error)) {
		return error.response || error.message;
	}

	return 'The profile request failed.';
}
